/* global Sys, Services, Platform, Resources  */
Sys.ns("Services");

/**
 * The game setting configuration.
 *
 * @typedef {Object} GameSetting
 * @property {string} id - The setting id.
 * @property {*} value - The value.
 * @property {boolean} [enabled=true] - Whether or not the setting is enabled for use.
 * @property {boolean} [persistent=false]  Whether or not to store the setting in local storage.
 */

/**
 * The setting configuration when adding a new game setting.
 *
 * @typedef {Object} AddGameSetting
 * @property {string} id - The setting id.
 * @property {string} languageID - The language id.
 * @property {*} value - The value.
 * @property {*} defaultValue - Initial default value.
 * @property {boolean} [enabled=true] - Whether or not the setting is enabled for use.
 * @property {boolean} [persistent=false]  Whether or not to store the setting in local storage.
 * @property {boolean} isGameSetting If it's a game setting.
 */

/**
 * The Game Settings Service.
 *
 * @class Services.GameSettings
 * @extends Services.Base
 */

Services.GameSettings = {
    constructor: function(config) {
        Services.GameSettings.superclass.constructor.apply(this, arguments);

        this._enabledSettings = [];
        this._gameSettings = [];
        this._settingsOpened = false;

        if (config.parsedResponse.g4mode) {
            this._isQuickSpinAvailable = false;
            this._isQuickSpinActive = false;
        }
        else if (!(typeof config.parsedResponse.config === "undefined") && !(typeof config.parsedResponse.config.gameParameters === "undefined")) {
            if (!(typeof config.parsedResponse.config.gameParameters.quickSpinAvailable === "undefined")) {
                this._isQuickSpinAvailable = config.parsedResponse.config.gameParameters.quickSpinAvailable;
                if (!(typeof config.parsedResponse.config.gameParameters.quickSpinActive === "undefined")) {
                    this._isQuickSpinActive = config.parsedResponse.config.gameParameters.quickSpinActive;
                }
            }
            else {
                this._isQuickSpinAvailable = true;
                this._isQuickSpinActive = false;
            }
        }
        else {
            this._isQuickSpinAvailable = true;
            this._isQuickSpinActive = false;
        }
        this._prepareGameSettings();
    },

    /**
     * Returns value with a proper type with JSON.parse.
     *
     * @param {any} value Stringified value of undefined type.
     * @returns {any} Recovered value with a proper type.
     * */
    _getValue: function(value) {
        var result;

        try {
            result = JSON.parse(value);
        }
        catch (e) {
            result = value;
        }
        if (value === "undefined") {
            return undefined;
        }
        return result;
    },

    /**
     * Set up game settings based on given default values and stored in localStorage.
     *
     * @private
     * @returns {void}
     * */
    _prepareGameSettings: function() {
        var me = this,
            defaultValues = [
                {
                    id: "quickSpin",
                    languageID: window.Language.Keys.setting_quickSpinGame,
                    defaultValue: me._isQuickSpinActive,
                    value: me._isQuickSpinActive,
                    persistent: true,
                    isGameSetting: true,
                    enabled: me._isQuickSpinAvailable
                },
                {
                    id: "spacebarToSpin",
                    languageID: window.Language.Keys.setting_spaceSpin,
                    defaultValue: false,
                    value: false,
                    persistent: true,
                    isGameSetting: true,
                    enabled: Platform.isDesktopDevice
                },
                {
                    id: "leftHandMode",
                    languageID: window.Language.Keys.setting_leftHandMode,
                    defaultValue: false,
                    value: false,
                    persistent: true,
                    isGameSetting: true,
                    enabled: !Platform.isDesktopDevice
                },
                {
                    id: "volume",
                    defaultValue: 1,
                    persistent: true,
                    isGameSetting: false,
                    value: 1
                },
                {
                    id: "volumeMute",
                    defaultValue: !Platform.isDesktopDevice,
                    persistent: true,
                    isGameSetting: false,
                    value: !Platform.isDesktopDevice
                }
            ];

        defaultValues.forEach(this._addSetting, this);
    },

    /**
     * Adds game setting and updates values.
     *
     * @private
     * @param {AddGameSetting} setting Setting object which will be added.
     * @returns {void}
     * */
    _addSetting: function(setting) {
        var value = setting.defaultValue,
            me = this,
            actualSetting,
            storedValue = me._getValue(
                me.getSetting(
                    setting.id,
                    true
                )
            );

        if (setting.id === "volumeMute" && !Platform.isDesktopDevice) {
            actualSetting = setting;
        }
        else {
            // Use value from local storage if defined
            if (setting.persistent && typeof storedValue !== "undefined") {
                value = storedValue;
            }

            actualSetting = Sys.apply(setting, { value: value });
        }

        me.setSetting({
            id: actualSetting.id,
            value: actualSetting.value,
            enabled: actualSetting.enabled,
            persistent: actualSetting.persistent
        });

        this._gameSettings.push(actualSetting);
    },

    /**
     * Adds game setting through private method and publishes event.
     *
     * @param {AddGameSetting} setting Setting object which will be added.
     * @returns {void}
     * */
    addSetting: function(setting) {
        this._addSetting(setting);
        this.publish("settingAdded", setting.id);
    },

    /**
     * Returns game setting.
     *
     * @private
     * @param {string} settingId Id of setting.
     * @returns {GameSetting} Game setting.
     * */
    _getGameSetting: function(settingId) {
        return this._gameSettings
            .filter(function(setting) {
                return setting && setting.id === settingId;
            })[0];
    },

    /**
     * Returns the set of game settings available.
     *
     * @returns {Array<{available:boolean, name:string, key: string}>} List of game settings set up for PlayUI adapter.
     * */
    getGameSettings: function() {
        return this._gameSettings
            .filter(function(setting) {
                return setting && setting.isGameSetting;
            })
            .map(function(setting) {
                return {
                    available: setting.enabled,
                    name: window.Services.language.getText(setting.languageID),
                    key: setting.id
                };
            });
    },

    _setupEvents: function() {
        this.observable.on({
            "notify:settingsManager.settingChanged": this._settingChanged.bind(this)
        });
    },

    /**
     * Returns if game settings is open.
     *
     * @returns {boolean} Game settings is open.
     */
    isOpen: function() {
        return this._open;
    },

    /**
     * Returns if any settings is open.
     *
     * @returns {boolean} If any settings is open in menu.
     */
    isAnySettingsOpen: function() {
        return this._settingsOpened;
    },

    /**
     * Opens the GameSettings (if not already open).
     *
     * @returns {void}
     */
    open: function() {
        if (this._open) {
            return;
        }

        this._open = true;

        this.publish("opened");
    },

    /**
     * Closes the GameSettings (if not already closed).
     *
     * @returns {void}
     */
    close: function() {
        if (!this._open) {
            return;
        }

        this._open = false;
        this.publish("closed");
    },

    /**
     * Checks if the setting exists.
     *
     * @param {string} setting The setting id.
     * @returns {boolean} Whether or not the setting exits.
     */
    hasSetting: function(setting) {
        return typeof this.getSetting(setting) !== "undefined";
    },

    /**
     * Gets the value of the setting.
     *
     * @param {string} setting The setting id.
     * @param {boolean?} storage Whether or not to read from local storage.
     * @param {boolean?} trueValue Define real value with a proper type or raw (string).
     * @returns {*} The value of the setting.
     */
    getSetting: function(setting, storage, trueValue) {
        var settingValue = Services.settingsManager.getSetting(setting);

        if (storage && Services.localStorageManager.hasData(setting)) {
            settingValue = Services.localStorageManager.readData(setting);
        }

        // When volume is muted, return 0 as volume setting.
        if (setting === "volume" && this.getSetting("volumeMute")) {
            settingValue = 0;
        }
        if (setting === "isCash" && !Sys.isDefined(settingValue)) {
            settingValue = Resources.readData("defaultBettingMode") === "cash";
        }

        if (trueValue) {
            return this._getValue(settingValue);
        }

        return settingValue;
    },

    /**
     * Checks if the setting is enabled.
     *
     * @param {string} setting The setting id.
     * @returns {boolean} Whether or not the setting is enabled.
     */
    isSettingEnabled: function(setting) {
        return this._enabledSettings.indexOf(setting) > -1;
    },

    /**
     * Set the setting by key value .
     *
     * @param {string} id The setting id.
     * @param {number} value The setting value.
     * @returns {void}
    */
    setSettingByKeyValue: function(id, value) {
        var gameSetting = this._getGameSetting(id);

        if (gameSetting) {
            this.setSetting({
                id: id,
                value: value,
                enabled: gameSetting.enabled,
                persistent: gameSetting.persistent
            });
        }
        else {
            /**
             * Unfortunately we have cases where we use setSetting or setSettingByKeyValue for
             * stuff that isn't a gameSetting. Probably to store a key and value with settingsmanager.
             * That means we won't always have a gameSetting object to get enabled and persistent from.
             */
            this.setSetting({
                id: id,
                value: value
            });
        }
    },

    /**
     * Set the setting.
     *
     * @param {GameSetting} setting The setting.
     * @returns {void}
     */
    setSetting: function(setting) {
        var id = setting.id,
            enabled = typeof setting.enabled === "undefined" || setting.enabled === true,
            persistent = setting.persistent === true;

        Services.settingsManager.storeSetting(id, setting.value, persistent);

        this._changeSettingStatus(id, enabled);
    },

    /**
     * Changes the status of the setting.
     *
     * @private
     * @param {string} id The setting id.
     * @param {boolean} enabled Whether or not the setting is enabled.
     * @returns {void}
     */
    _changeSettingStatus: function(id, enabled) {
        var index = this._enabledSettings.indexOf(id);

        if (!this.hasSetting(id)) {
            return;
        }

        if (enabled && index === -1) {
            this._enabledSettings.push(id);
        }
        else if (!enabled && index > -1) {
            this._enabledSettings.splice(index, 1);
        }
    },

    /**
     * Handles when a setting has changed.
     *
     * @private
     * @param {string} setting The setting id.
     * @param {*} value The new value of the setting.
     * @returns {void}
     */
    _settingChanged: function(setting, value) {
        this.publish("changed", {
            id: setting,
            value: value
        });
    },

    /**
     * Handles settings opened/closed view.
     *
     * @param {string} view View.
     * @returns {void}
     */
    setViewState: function(view) {
        if (view !== "DEFAULT" && !this._settingsOpened) {
            this._settingsOpened = true;
            this.publish("settingsOpened");
        }
        else if (view === "DEFAULT") {
            this._settingsOpened = false;
            this.publish("settingsClosed");
        }
    }
};

Services.GameSettings = Sys.extend(Services.Base, Services.GameSettings, "Services.GameSettings");
